import {
  ChangeDetectorRef,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  effect,
  ElementRef,
  inject,
  Input,
  input,
  InputSignal,
  OnDestroy,
  OnInit,
  signal,
  viewChild,
} from '@angular/core';
import {I18nModule, LanguageService, Product, User} from '@spartacus/core';
import {UserAccountFacade} from '@spartacus/user/account/root';
import {combineLatest, Observable} from 'rxjs';
import {map, tap} from 'rxjs/operators';
import {v4 as uuidV4} from 'uuid';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';

import {ConfigService, DcpBundleTemplate, FeatureTogglesService} from '@dcp/configurator';
import {DcpAuthService} from '@app/auth/services/dcp-auth.service';
import {DcpProductUtilitiesService} from '@app/product/services/dcp-product-utilities.service';
import {UNLEASH_TOGGLES} from '@app/config/FeatureToggleConfig';
import {CampaignTrackingService} from '@app/services/campaign-tracking.service';
import {CtaWrapperButtonSection} from '@app/product/models/ctaButtonContext.model';
import {DcpSupportFormService} from '@app/shared/support-form/dcp-support-form.service';
import {OAuthService} from 'angular-oauth2-oidc';
import {LeadFormLuigiContext} from '@app/product/models/lead.model';

const ctaWebcomponentConfigKey = 'webComponents.ctaButton';
const reCaptchaSiteKeyConfigKey = 'RE_CAPTCHA_SITE_KEY';
// Event dispatched by the cta-app web component when an unauthenticated user
// clicks a PARTNER_PRIVATE_OFFER CTA. The host app must trigger the OIDC login
// flow in response — the web component cannot do this itself because it has no
// access to the host's auth service.
const FORCE_LOGIN_EVENT = 'dcp-private-offer-form:force-login';
// Maximum time to wait for the cta-app micro frontend to fire luigi-container's
// 'initialized' event. If it never fires (script load failure, network blip, etc.)
// we surface a contact-support fallback instead of a permanently empty slot.
const FALLBACK_TIMEOUT_MS = 15000;

@UntilDestroy()
@Component({
  selector: 'dcp-cta-webcomponent-wrapper',
  templateUrl: './cta-webcomponent-wrapper.component.html',
  imports: [I18nModule],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class CtaWebcomponentWrapperComponent implements OnInit, OnDestroy {
  private readonly campaignTrackingService = inject(CampaignTrackingService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly configService = inject(ConfigService);
  private readonly dcpProductUtilitiesService = inject(DcpProductUtilitiesService);
  private readonly dcpSupportFormService = inject(DcpSupportFormService);
  private readonly authService = inject(DcpAuthService);
  private readonly languageService = inject(LanguageService);
  private readonly userFacade = inject(UserAccountFacade);
  private readonly togglesService = inject(FeatureTogglesService);
  private readonly oAuthService = inject(OAuthService);

  isHeroSection = input<boolean>(false);
  isPublisherSection = input<boolean>(false);
  product: InputSignal<Product | undefined> = input<Product | undefined>(undefined);
  ctaSelectionModalEnabled = input<boolean>(false);
  @Input() bundleTemplate: DcpBundleTemplate;

  readonly ctaButtonId = signal<string>(uuidV4());
  readonly gRecaptchaId = input<string>('g-recaptcha-cta-wrapper');
  readonly showFallback = signal<boolean>(false);

  public currentLanguage = signal<string>('en');
  public leadFormWebComponentCaptchaFeatureToggle = signal<boolean>(false);
  public url = signal<string | undefined>(this.configService.getString(ctaWebcomponentConfigKey));
  public user = signal<User | undefined>(undefined);
  public siteKey = signal<string | undefined>(this.configService.getString(reCaptchaSiteKeyConfigKey));

  private readonly luigiContainerRef = viewChild<ElementRef<HTMLElement>>('luigiContainer');
  private fallbackTimeoutId: ReturnType<typeof setTimeout> | undefined;
  private webComponentInitialized = false;
  private lastRenderedLanguage: string | undefined;
  private readonly onLuigiInitialized = (): void => {
    this.webComponentInitialized = true;
    this.clearFallbackTimeout();
    // If the timeout already elapsed and the fallback was shown, the cta-app
    // recovered after the deadline — hide the fallback so the user doesn't see
    // both the working CTA and the support button.
    if (this.showFallback()) {
      this.showFallback.set(false);
      this.cdr.detectChanges();
    }
  };

  private readonly onForceLogin = (): void => {
    this.authService.login();
  };

  leadFormCaptcha$: Observable<boolean> = this.togglesService
    .getFeatureToggles()
    .pipe(
      map((toggles) => toggles.find((toggle) => toggle.name === UNLEASH_TOGGLES.leadFormCaptcha)?.enabled ?? false),
    );

  constructor() {
    // Attach the 'initialized' listener as soon as the luigi-container element is
    // in the DOM. Previously we polled with querySelector inside the container's
    // shadow root, which doesn't pierce shadow DOM and so always fell through to
    // the fallback even when the cta-app rendered correctly.
    //
    // Use onCleanup so that if the viewChild ref ever transitions (e.g., the
    // toggle flips off then on, re-creating the element) we detach the listener
    // from the previous element instead of stacking listeners on each transition.
    effect((onCleanup) => {
      const ref = this.luigiContainerRef();
      if (ref) {
        const el = ref.nativeElement;
        el.addEventListener('initialized', this.onLuigiInitialized);
        onCleanup(() => el.removeEventListener('initialized', this.onLuigiInitialized));
      }
    });
  }

  ngOnInit() {
    // Listen for the custom event fired by the cta-app web component when a login is required (e.g. private offer)
    window.addEventListener(FORCE_LOGIN_EVENT, this.onForceLogin);

    combineLatest([this.leadFormCaptcha$, this.userFacade.get(), this.languageService.getActive()])
      .pipe(
        tap(([lfCaptcha, user, language]) => {
          this.currentLanguage.set(language);
          this.leadFormWebComponentCaptchaFeatureToggle.set(lfCaptcha);
        }),
        untilDestroyed(this),
      )
      .subscribe(([lfCaptcha, user, language]) => {
        // If the language changed after a successful first render, the
        // luigi-container is re-mounted with a new context. Reset state so the
        // fallback timer arms again and recovers if the second mount fails.
        if (this.lastRenderedLanguage !== undefined && this.lastRenderedLanguage !== language) {
          this.webComponentInitialized = false;
          if (this.showFallback()) {
            this.showFallback.set(false);
          }
          this.clearFallbackTimeout();
        }
        this.lastRenderedLanguage = language;

        this.user.set(user);
        this.cdr.detectChanges();
        // Start the fallback timer once we know the web component will be rendered.
        // If the cta-app never fires 'initialized' within the timeout we show
        // the contact-support fallback
        this.startFallbackTimeout();
      });
  }

  ngOnDestroy(): void {
    this.clearFallbackTimeout();
    // The 'initialized' listener on the luigi-container element is removed by
    // the effect's onCleanup callback when the component is destroyed.
    window.removeEventListener(FORCE_LOGIN_EVENT, this.onForceLogin);
  }

  generateLuigiHtmlId() {
    if (this.isHeroSection()) {
      return `dcp-hero-form-button-${this.ctaButtonId()}`;
    }
    if (this.isPublisherSection()) {
      return `dcp-publisher-form-button-${this.ctaButtonId()}`;
    }
    return `dcp-plans-form-button-${this.ctaButtonId()}`;
  }

  getCtaWebcomponentContext() {
    const contextDetails = {
      idToken: this.oAuthService.getAccessToken(),
    } as LeadFormLuigiContext;

    const canonicalId = this.dcpProductUtilitiesService.getCanonicalId(null, this.bundleTemplate);
    const internalId =
      canonicalId.entitlementRuleInternalId ?? this.resolveInternalIdFromRuleCode(canonicalId.entitlementRuleCode);

    // Read redirect config using getObject to get the entire config object
    const redirectConfig = this.configService.getObject('webComponentsConfig.localRedirect', {
      enabled: false,
      origin: '',
    });
    const redirectEnabled = redirectConfig?.enabled ?? false;
    const redirectOrigin = redirectConfig?.origin ?? '';

    const context = {
      ...(internalId ? {internalId} : {}),
      ctaId: this.ctaButtonId(),
      idToken: contextDetails.idToken,
      technicalName: this.dcpProductUtilitiesService.getProductId(this.product()),
      campaignData: this.campaignTrackingService.getCampaignData(),
      languageIso: this.currentLanguage(),
      customerId: this.user()?.scimUserId,
      gRecaptchaSiteKey: this.siteKey(),
      gRecaptchaId: this.gRecaptchaId(),
      maps: window?.google?.maps ?? undefined,
      section: this.selectSection(),
      ctaSelectionModalEnabled: this.ctaSelectionModalEnabled(),
      productCode: this.dcpProductUtilitiesService.getProductCode(this.product()),
      ...(redirectEnabled && {
        localConfiguratorRedirectEnabled: true,
        localConfiguratorRedirectOrigin: redirectOrigin,
      }),
    };

    return context;
  }

  openSupportFormFallback(): void {
    this.dcpSupportFormService.open();
  }

  private selectSection(): CtaWrapperButtonSection {
    if (this.isPublisherSection()) {
      return CtaWrapperButtonSection.publisherSection;
    } else if (this.isHeroSection()) {
      return CtaWrapperButtonSection.heroSection;
    }

    return CtaWrapperButtonSection.plansAndPricingSection;
  }

  // Numeric "<productId>_<materialNumber>" entitlementRuleCodes (e.g. SAP BTP
  // "8900252_8016044") must be reduced to the bare material-number segment —
  // the cta-app's material filter on the CTA backend is keyed by materialNumber
  // (e.g. "8016044"), and forwarding the whole code yields VAL_0044
  // ("Material specified in request filter is not found on product").
  //
  // Anything else (single- or multi-segment opaque rule codes such as
  // "kjowPKz_Q5mlw9_NJjx5kg" or "cxN8l1MiSLqwt7Q-Hk-Hnw") is forwarded
  // verbatim — that is the shape DCP-51122 needs.
  private resolveInternalIdFromRuleCode(entitlementRuleCode: string | undefined): string | undefined {
    if (!entitlementRuleCode) {
      return entitlementRuleCode;
    }
    return /^\d+_\d+$/.test(entitlementRuleCode) ? entitlementRuleCode.split('_')[1] : entitlementRuleCode;
  }

  private startFallbackTimeout(): void {
    if (this.fallbackTimeoutId !== undefined || this.webComponentInitialized) {
      return;
    }
    this.fallbackTimeoutId = setTimeout(() => {
      this.fallbackTimeoutId = undefined;
      if (!this.webComponentInitialized) {
        this.showFallback.set(true);
        this.cdr.detectChanges();
      }
    }, FALLBACK_TIMEOUT_MS);
  }

  private clearFallbackTimeout(): void {
    if (this.fallbackTimeoutId !== undefined) {
      clearTimeout(this.fallbackTimeoutId);
      this.fallbackTimeoutId = undefined;
    }
  }
}
